import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import { useVirtuosoScrollToIndex } from "../../../hooks/useVirtuosoScrollToIndex";
import { useLazyAboEnrichment } from "../../../hooks/useLazyAboEnrichment";
import { useAppSelector } from "../../../../../store";
import { storedLocale } from "../../../../Authentication/util";
import { ActionReportProfileSection } from "../../ActionReportProfileSection/ActionReportProfileSection";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActionReportListProps, ActionReportRow, FieldConfig } from "../types";
import type { ProfileSectionTags } from "../../ActionReportProfileSection/ActionReportProfileSection";
import "./DesktopTable.scss";
import { getKpiValue } from "../../KPI";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/actionReports";
import watchlistContent from "../../../../../content/watchlistNotifications";
import { useNoDataAvailableLabel } from "../../../../../common/hooks/useNoDataAvailableLabel";
import NoDataValue from "../../../../../common/components/NoDataValue/NoDataValue";
import { useActionReportList } from "../../../hooks/useActionReportList";
import { getAffAboId, isChinaAff, scrollClearOfStickyNav } from "../utils/actionReportUtils";
import { TFunction } from "i18next";
import { useWatchlistToggle } from "../../../hooks/useWatchlistToggle";
import { IconSortUpDown } from "../../../../../common/icons/IconSortUpDown/IconSortUpDown";
import { useLazyProfileImages } from "../../../hooks/useLazyProfileImages";
import { useLazyWhatsAppEnrichment } from "../../../hooks/useLazyWhatsAppEnrichment";
import { CUSTOMER_BUSINESS_NATURES } from "../../../../../common/enums/businessNatures";
import LeafToastMessage from "../../../../../common/leaf/LeafToastMessage/LeafToastMessage";

type DesktopTableRowProps = {
  index: number;
  row: ActionReportRow;
  selectedKpis: string[];
  kpisToFetch: string[];
  reportId: string;
  period: string;
  fieldConfig: FieldConfig[];
  skipEnrichment?: boolean;
  enrichWhatsApp?: boolean;
  awaitAutoEnrichment?: boolean;
  t: TFunction;
  noDataAvailableLabel: string;
  columnWidths: number[];
  showABOProfile: (event: CustomEventInit) => void;
  isLastRow: boolean;
  isSelected?: boolean;
  rowInViewRef?: React.RefObject<{ index: number; timestamp: number } | null>;
  batchSize?: number;
  enableWatchlist?: boolean;
  trackABONameClick?: Function;
  onProfileTrack?: Function;
  onWatchlistTrack?: Function;
  onPhoneTrack?: () => void;
  onMessageTrack?: () => void;
  onWhatsAppTrack?: () => void;
  showImageBlock?: boolean;
  showTagForDeepCustomers?: boolean;
  onWatchlistNotification?: (name: string, wasInWatchlist: boolean, success: boolean) => void;
};

const TableRow = ({
  index,
  row,
  selectedKpis,
  kpisToFetch,
  enableWatchlist = false,
  reportId,
  period,
  batchSize,
  fieldConfig,
  t,
  noDataAvailableLabel,
  columnWidths,
  showABOProfile,
  skipEnrichment = false,
  enrichWhatsApp = false,
  awaitAutoEnrichment = false,
  isLastRow = false,
  isSelected = false,
  rowInViewRef,
  trackABONameClick = () => {},
  onProfileTrack = () => {},
  onWatchlistTrack = () => {},
  onPhoneTrack,
  onMessageTrack,
  onWhatsAppTrack,
  showImageBlock = false,
  showTagForDeepCustomers = false,
  onWatchlistNotification,
}: DesktopTableRowProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const aboNameRef = useRef<HTMLButtonElement>(null);

  const hideWatchlistButton =
    !enableWatchlist || (CUSTOMER_BUSINESS_NATURES.has(Number(row.businessNature)) && row && Number(row.level) >= 2);

  const isWatchlistReport = reportId === "watchlist";

  useEffect(() => {
    if (!isSelected) return;

    // scrollIntoView on the actual DOM element is more reliable than Virtuoso's
    // estimated scrollToIndex for items with non-uniform heights (e.g. "New Customer").
    // Virtuoso's first scroll gets us close; this corrects to the exact position and
    // signals useVirtuosoScrollToIndex's remaining retries to stop, since they'd
    // otherwise re-estimate and visibly fight this correction.
    // "end" (bottom-align) instead of "nearest" so the result is deterministic across
    // browsers/devices — "nearest" picks top-vs-bottom based on scroll position at the
    // moment it runs, which iOS Safari on a real device resolves differently than the
    // Simulator, sometimes top-aligning the row under the sticky nav.
    if (ref.current) {
      ref.current.scrollIntoView({ block: "end", behavior: "instant" });
      scrollClearOfStickyNav(ref.current);
    }
    if (aboNameRef.current) {
      aboNameRef.current.focus({ preventScroll: true });
    }
    if (rowInViewRef && isSelected) {
      rowInViewRef.current = { index: index, timestamp: Date.now() };
    }
  }, [index, isSelected, rowInViewRef]);
  const { user } = useAppSelector((state) => state.boot);

  const { isInWatchlist, toggleWatchlist } = useWatchlistToggle({ row });

  const { getEnrichedAbo, getFetchState } = useLazyAboEnrichment({
    reportId,
    period,
    kpis: kpisToFetch,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
    batchSize,
    skipEnrichment,
  });

  const affAboId = getAffAboId(row);
  const isChina = isChinaAff(row.aff);
  const fetchState = affAboId ? getFetchState(affAboId) : "unfetched";
  const enrichedData = affAboId ? getEnrichedAbo(affAboId) : null;

  // Profile image from lazy-loaded store
  const { getProfileImage, fetchImages } = useLazyProfileImages();
  const profileImageBase64 = affAboId ? getProfileImage(affAboId) : undefined;
  const photoUrl =
    !row.isConfidential && profileImageBase64 ? `data:image/png;base64,${profileImageBase64}` : undefined;

  // WhatsApp number from independent lazy enrichment, when enabled for this report
  const { getWhatsAppNumber, getWhatsAppFetchState } = useLazyWhatsAppEnrichment({
    period,
    locale: storedLocale(),
    isoCountryCode: user.isoCountryCode || "US",
  });
  const whatsAppNumber = enrichWhatsApp
    ? affAboId
      ? getWhatsAppNumber(affAboId)
      : undefined
    : (row.whatsapp ?? enrichedData?.whatsapp);
  const whatsAppLoading = enrichWhatsApp && !isChina && !!affAboId && getWhatsAppFetchState(affAboId) === "fetching";

  // If enrichment provides imageRefURL and we haven't fetched the image yet, trigger fetch
  useEffect(() => {
    if (row.isConfidential || !affAboId || profileImageBase64) return;
    const enrichedImageRef = enrichedData?.imageRefURL;
    if (enrichedImageRef && !row.imageRefUrl) {
      fetchImages([{ ...row, imageRefUrl: enrichedImageRef }]);
    }
  }, [enrichedData?.imageRefURL, affAboId, profileImageBase64, row, fetchImages]);

  // Prefer the enriched name when present, but treat whitespace-only values
  // as "no name" so the row falls back to the renewals/whosnew record's own
  // name field. Backend also returns the literal string "Confidential User"
  // for privacy-flagged ABOs — translate that for display.
  const enrichedName = enrichedData?.aboName?.trim() ? enrichedData.aboName : "";
  const rawName = row.isConfidential || row.allowCustomerDetails ? row.aboName : enrichedName || row.aboName;
  const displayName =
    rawName === "Confidential User" || row.isConfidential
      ? t("ConfidentialUser", "Confidential User", { ns: "leafAboName" })
      : rawName;

  const handleAboNameClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onProfileTrack();
    showABOProfile({
      detail: {
        abo: {
          affAbo: affAboId,
          isCustomer: row.isCustomer,
          isConfidential: row.isConfidential,
          level: row.level,
          allowCustomerDetails: row.allowCustomerDetails,
        },
        target: e.currentTarget,
      },
    });
    trackABONameClick();
  };

  const handleLineagePillClick = (clickedAffAbo: string) => {
    onProfileTrack();
    showABOProfile({
      detail: {
        abo: { affAbo: clickedAffAbo, isCustomer: false, isConfidential: false, level: undefined },
        target: undefined,
      },
    });
  };

  const onWatchListToggle = async () => {
    const wasInWatchlist = isInWatchlist;
    onWatchlistTrack(!isInWatchlist);
    try {
      await toggleWatchlist();
      onWatchlistNotification?.(displayName, wasInWatchlist, true);
    } catch {
      onWatchlistNotification?.(displayName, wasInWatchlist, false);
    }
  };

  const rowClassName = [
    "ActionReportTableView__row",
    isLastRow ? "ActionReportTableView__row--last" : "",
    isChina ? "ActionReportTableView__row--china" : "",
    isSelected ? "ActionReportTableView__row--selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={ref} className={rowClassName}>
      <div className="ActionReportTableView__tile">
        <div
          className="ActionReportTableView__cell ActionReportTableView__cell--profile"
          style={{
            width: columnWidths[0] ? `${columnWidths[0]}px` : "auto",
            minWidth: columnWidths[0] ? `${columnWidths[0]}px` : "auto",
            maxWidth: columnWidths[0] ? `${columnWidths[0]}px` : "auto",
          }}
        >
          <ActionReportProfileSection
            variant="desktop"
            aboName={displayName}
            aboNumber={row.aboNumber}
            allowCustomerDetails={row.allowCustomerDetails}
            aff={row.aff}
            abo={row.aboNumber}
            aboOnclick={handleLineagePillClick}
            includeGlobalMB={isWatchlistReport}
            rankBadge={row.level}
            photoUrl={photoUrl}
            isChina={isChina}
            isInternational={row.isInternational}
            countryCode={row.countryCode}
            tags={{
              isProvisional: row.isProvisional,
              isFirstContractYear: enrichedData?.isFirstContractYear,
              businessNatureTag: row.businessNatureTag as ProfileSectionTags["businessNatureTag"],
              conversionType: row.conversionType,
              upForRenewal: row.upForRenewal as boolean | undefined,
              level: row.level,
              businessNature: row.businessNature,
              showTagForDeepCustomers,
              allowCustomerDetails: row.allowCustomerDetails,
            }}
            watchlist={!hideWatchlistButton ? isInWatchlist : undefined}
            toggleWatchlist={!hideWatchlistButton ? onWatchListToggle : undefined}
            onAboNameClick={handleAboNameClick}
            isLoading={
              !isChina &&
              ((skipEnrichment === false && (fetchState === "fetching" || !enrichedData)) ||
                (awaitAutoEnrichment && !enrichedData))
            }
            phoneNumber={row.isConfidential ? undefined : (row.phoneNumber ?? enrichedData?.phoneNumber)}
            onPhoneTrack={row.isConfidential ? undefined : onPhoneTrack}
            onMessageTrack={row.isConfidential ? undefined : onMessageTrack}
            whatsAppNumber={row.isConfidential ? undefined : whatsAppNumber}
            whatsAppLoading={row.isConfidential ? false : whatsAppLoading}
            onWhatsAppTrack={row.isConfidential ? undefined : onWhatsAppTrack}
            showImageBlock={showImageBlock}
            privacyFlag={row.isConfidential ?? false}
          />
        </div>
        {selectedKpis.map((column, colIndex) => {
          const field = fieldConfig.find(({ kpi }) => kpi === column);
          const desktopCellClassName = field?.desktopCellClassName;
          const rowValue = String(row[column] ?? "");
          const showLoader =
            !isChina &&
            ((skipEnrichment === false && (fetchState === "fetching" || !enrichedData)) ||
              (awaitAutoEnrichment && !enrichedData));

          const kpiResult = field?.renderValue
            ? field.renderValue(rowValue, row)
            : !isChina
              ? getKpiValue(
                  field?.id || column,
                  rowValue,
                  skipEnrichment,
                  enrichedData || undefined,
                  storedLocale(),
                  row.businessNature,
                )
              : null;
          const isNoData = kpiResult === "--";

          return (
            <div
              key={colIndex}
              className={`ActionReportTableView__cell${desktopCellClassName ? ` ${desktopCellClassName}` : ""}`}
              style={{
                width: columnWidths[colIndex + 1] ? `${columnWidths[colIndex + 1]}px` : "auto",
                minWidth: columnWidths[colIndex + 1] ? `${columnWidths[colIndex + 1]}px` : "auto",
                maxWidth: columnWidths[colIndex + 1] ? `${columnWidths[colIndex + 1]}px` : "auto",
              }}
            >
              {isChina ? (
                ""
              ) : showLoader ? (
                <LeafRectangleLoader isLoading width="6.25rem" height="1rem" margin="0" />
              ) : isNoData ? (
                <NoDataValue label={noDataAvailableLabel} />
              ) : (
                kpiResult
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DesktopTable = ({
  rows,
  selectedKpis,
  enableWatchlist = false,
  reportId,
  period,
  batchSize = 10,
  fieldConfig,
  showABOProfile,
  aboHeader = "aboNameNumber",
  skipEnrichment = false,
  enrichWhatsApp = false,
  awaitAutoEnrichment = false,
  scrollToIndex,
  trackABONameClick = () => {},
  onProfileTrack,
  onWatchlistTrack,
  onPhoneTrack,
  onMessageTrack,
  onWhatsAppTrack,
  onColumnHeaderClick,
  columnSortState,
  showImageBlock = false,
  showTagForDeepCustomers = false,
}: ActionReportListProps) => {
  const { t } = useTranslation(["actionReports", "leafAboName", "watchlistNotifications"]);
  const noDataAvailableLabel = useNoDataAvailableLabel();
  const headerRef = useRef<HTMLTableRowElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { virtuosoRef, rowInViewRef } = useVirtuosoScrollToIndex(scrollToIndex, containerRef);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [toastState, setToastState] = useState<"success" | "error">("success");
  const [showToast, setShowToast] = useState(false);
  const [toastName, setToastName] = useState("");
  const [toastWasInWatchlist, setToastWasInWatchlist] = useState(false);
  const [toastCounter, setToastCounter] = useState(0);

  const { kpisToFetch, onRowsRendered } = useActionReportList({
    rows,
    reportId,
    period,
    fieldConfig,
    batchSize,
    skipEnrichment,
    enrichWhatsApp,
  });

  const handleWatchlistNotification = useCallback((name: string, wasInWatchlist: boolean, success: boolean) => {
    setToastName(name);
    setToastWasInWatchlist(wasInWatchlist);
    setToastState(success ? "success" : "error");
    setToastCounter((prev) => prev + 1);
    setShowToast(true);
  }, []);

  const handleVisibleRowsChanged = useCallback(
    (range: { startIndex: number; endIndex: number }) => {
      onRowsRendered({ startIndex: range.startIndex, stopIndex: range.endIndex });
    },
    [onRowsRendered],
  );

  // Measure header column widths after render
  const measureColumnWidths = useCallback(() => {
    if (headerRef.current) {
      const cells = headerRef.current.querySelectorAll("th");
      const widths = Array.from(cells).map((cell) => cell.offsetWidth);
      setColumnWidths(widths);
    }
    if (tableRef.current) {
      setTableWidth(tableRef.current.offsetWidth);
    }
  }, []);

  // Use useLayoutEffect for initial measurement to prevent visual flicker
  // This runs synchronously after DOM mutations but before browser paint
  useLayoutEffect(() => {
    measureColumnWidths();
  }, [measureColumnWidths, selectedKpis, enableWatchlist]);

  // ResizeObserver catches layout-driven width changes (e.g. the side nav opening/closing)
  // that don't fire a window "resize" event, since the viewport itself hasn't changed size.
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => measureColumnWidths());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [measureColumnWidths]);

  const columnCount = selectedKpis.length;

  // Hide rows until column widths are measured to prevent layout shift
  const isReady = columnWidths.length > 0;

  const getHeaderText = () => {
    switch (aboHeader) {
      case "applicantName":
        return t("applicantName", defaultContent["applicantName"] as string);
      case "nameNumber":
        return t("nameNumber", defaultContent["nameNumber"] as string);
      default:
        return t("aboNameNumber", defaultContent["aboNameNumber"] as string);
    }
  };

  return (
    <>
      <LeafToastMessage
        showToast={showToast}
        theme={toastState}
        hideProgressBar={true}
        className="mini-notification"
        toastId={`watchlist-notification-${toastCounter}`}
        htmlContent={
          toastState === "success" ? (
            <span>
              {t(
                `watchlistNotifications:notification.${toastWasInWatchlist ? "removed" : "added"}`,
                toastWasInWatchlist ? watchlistContent.notification.removed : watchlistContent.notification.added,
                { name: toastName },
              )}
            </span>
          ) : (
            <span>{t("watchlistNotifications:notification.error", watchlistContent.notification.error)}</span>
          )
        }
      />
      <div
        ref={containerRef}
        className="ActionReportTableView"
        style={{ "--max-column-width": columnCount >= 7 ? "12rem" : "20rem" } as React.CSSProperties}
      >
        <table ref={tableRef} className="ActionReportTableView__table">
          <thead>
            <tr ref={headerRef} className="ActionReportTableView__header">
              {(() => {
                const nameField = fieldConfig.find(({ kpi }) => kpi === "aboName");
                const isNameSortable = nameField?.sortable && onColumnHeaderClick;
                const nameSortDirection = columnSortState?.["aboName"];

                return (
                  <th
                    className={`ActionReportTableView__cell${isNameSortable ? " ActionReportTableView__cell--clickable" : ""}`}
                    onClick={isNameSortable ? () => onColumnHeaderClick("aboName") : undefined}
                    style={isNameSortable ? { cursor: "pointer" } : undefined}
                    data-testid="table-column-aboName"
                  >
                    <span className="ActionReportTableView__cellLabel">
                      {getHeaderText()}
                      {isNameSortable && (
                        <IconSortUpDown
                          width="0.875rem"
                          height="0.875rem"
                          direction={nameSortDirection}
                          activeColor="#fff"
                          mutedColor="rgba(255, 255, 255, 0.5)"
                          neutralColor="#fff"
                        />
                      )}
                    </span>
                  </th>
                );
              })()}
              {selectedKpis.map((column, index) => {
                const field = fieldConfig.find(({ kpi }) => kpi === column);
                const title = field?.title || column;
                const isSortable = field?.sortable && onColumnHeaderClick;
                const sortDirection = columnSortState?.[column];

                const desktopCellClassName = field?.desktopCellClassName;
                return (
                  <th
                    key={index}
                    className={`ActionReportTableView__cell${desktopCellClassName ? ` ${desktopCellClassName}` : ""}${isSortable ? " ActionReportTableView__cell--clickable" : ""}`}
                    onClick={isSortable ? () => onColumnHeaderClick(column) : undefined}
                    style={{ cursor: isSortable ? "pointer" : undefined }}
                    data-testid={`table-column-${column}`}
                  >
                    <span className="ActionReportTableView__cellLabel">
                      {title}
                      {isSortable && (
                        <IconSortUpDown
                          width="0.875rem"
                          height="0.875rem"
                          direction={sortDirection}
                          activeColor="#fff"
                          mutedColor="rgba(255, 255, 255, 0.5)"
                          neutralColor="#fff"
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
        </table>
        <div
          style={{
            width: tableWidth || "100%",
            visibility: isReady ? "visible" : "hidden",
          }}
        >
          <Virtuoso
            ref={virtuosoRef}
            useWindowScroll
            data={rows}
            overscan={200}
            rangeChanged={handleVisibleRowsChanged}
            itemContent={(index, row) => (
              <TableRow
                key={getAffAboId(row) || index}
                index={index}
                row={row}
                selectedKpis={selectedKpis}
                kpisToFetch={kpisToFetch}
                enableWatchlist={enableWatchlist}
                reportId={reportId}
                period={period}
                batchSize={batchSize}
                fieldConfig={fieldConfig}
                columnWidths={columnWidths}
                showABOProfile={showABOProfile}
                skipEnrichment={skipEnrichment}
                enrichWhatsApp={enrichWhatsApp}
                awaitAutoEnrichment={awaitAutoEnrichment}
                isLastRow={index === rows?.length - 1}
                isSelected={index === scrollToIndex}
                rowInViewRef={rowInViewRef}
                t={t}
                noDataAvailableLabel={noDataAvailableLabel}
                trackABONameClick={trackABONameClick}
                onProfileTrack={onProfileTrack}
                onWatchlistTrack={onWatchlistTrack}
                onPhoneTrack={onPhoneTrack}
                onMessageTrack={onMessageTrack}
                onWhatsAppTrack={onWhatsAppTrack}
                showImageBlock={showImageBlock}
                showTagForDeepCustomers={showTagForDeepCustomers}
                onWatchlistNotification={handleWatchlistNotification}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};
